import React, { useMemo, memo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface ChartData {
  series: any[];
  categories?: string[] | null;
}

interface ChartRendererProps {
  type: string;
  title?: string;
  data: ChartData;
  height?: number;
}

const ChartRenderer: React.FC<ChartRendererProps> = ({ type, title, data, height = 350 }) => {
  // Adjust height for better visibility in fullscreen
  const adjustedHeight = height > 400 ? height : height;
  
  const chartOptions: ApexOptions = useMemo(() => {
    const baseOptions: ApexOptions = {
      chart: {
        type: type as any,
        height: adjustedHeight,
        fontFamily: 'inherit',
        toolbar: { show: true },
        width: '100%'
      },
      title: title ? { text: title, style: { fontSize: '16px', fontWeight: 600 } } : undefined,
      colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#546E7A'],
      dataLabels: { enabled: ['pie', 'donut', 'radialBar'].includes(type) },
      legend: { show: true, position: 'bottom' },
      responsive: [{
        breakpoint: 480,
        options: {
          legend: {
            position: 'bottom'
          }
        }
      }]
    };

    // Chart-specific configurations
    switch (type) {
      case 'pie':
      case 'donut':
      case 'polarArea':
      case 'radialBar':
        baseOptions.labels = data.categories || [];
        if (type === 'donut') {
          baseOptions.plotOptions = { pie: { donut: { size: '70%' } } };
        }
        break;

      case 'heatmap':
        // Calculate dynamic color scale based on data values
        const allValues = data.series.flatMap(series => series.data.map((d: any) => d.y)).filter((v: any) => typeof v === 'number');
        const hasZeros = allValues.includes(0);
        const nonZeroValues = hasZeros ? allValues.filter((v: any) => v > 0) : allValues;
        
        const minValue = nonZeroValues.length > 0 ? Math.min(...nonZeroValues) : 1;
        const maxValue = nonZeroValues.length > 0 ? Math.max(...nonZeroValues) : 1;
        const range = maxValue - minValue;
        const step = range / 4;
        
        // Build color scale with special handling for zero values
        const colorRanges: any[] = [];
        
        // Add zero range if zeros exist
        if (hasZeros) {
          colorRanges.push({
            from: -0.5,
            to: 0.5,
            color: '#E5E7EB',
            name: 'No Data'
          });
        }
        
        // Add gradient ranges for non-zero values
        if (range > 0) {
          colorRanges.push(
            { from: minValue, to: minValue + step, color: '#00D9FF', name: 'Low' },
            { from: minValue + step, to: minValue + step * 2, color: '#00A100', name: 'Medium-Low' },
            { from: minValue + step * 2, to: minValue + step * 3, color: '#FFB200', name: 'Medium-High' },
            { from: minValue + step * 3, to: maxValue, color: '#FF4560', name: 'High' }
          );
        } else {
          // Single value case
          colorRanges.push({
            from: minValue,
            to: maxValue,
            color: '#00D9FF',
            name: 'Value'
          });
        }
        
        baseOptions.plotOptions = {
          heatmap: {
            shadeIntensity: 0.5,
            radius: 2,
            useFillColorAsStroke: false,
            colorScale: {
              ranges: colorRanges
            },
            distributed: false,
            enableShades: true,
            reverseNegativeShade: false
          }
        };
        
        // Add data labels for heatmap (hide labels for zero values)
        baseOptions.dataLabels = {
          enabled: true,
          style: {
            colors: ['#fff'],
            fontSize: '11px',
            fontWeight: '600'
          },
          formatter: (val: number) => val === 0 ? '' : val.toString()
        };
        
        // Configure tooltip
        baseOptions.tooltip = {
          y: {
            formatter: (val: number) => val === 0 ? 'No data' : `${val}`
          }
        };
        break;

      case 'treemap':
        baseOptions.plotOptions = {
          treemap: {
            enableShades: true,
            shadeIntensity: 0.5
          }
        };
        break;

      case 'radar':
        baseOptions.xaxis = { categories: data.categories || [] };
        baseOptions.plotOptions = {
          radar: {
            size: 140,
            polygons: { strokeColors: '#e9e9e9', fill: { colors: ['#f8f8f8', '#fff'] } }
          }
        };
        break;

      case 'boxplot':
        baseOptions.plotOptions = {
          boxPlot: {
            colors: { upper: '#5C4742', lower: '#A5978B' }
          }
        };
        break;

      case 'candlestick':
        baseOptions.xaxis = { type: 'datetime' };
        baseOptions.plotOptions = {
          candlestick: {
            colors: { upward: '#00B746', downward: '#EF403C' }
          }
        };
        break;

      case 'rangeBar':
      case 'timeline':
        baseOptions.plotOptions = {
          bar: {
            horizontal: true,
            barHeight: '80%',
            rangeBarGroupRows: true
          }
        };
        baseOptions.xaxis = { type: 'datetime' };
        break;

      default:
        // For series-based charts (line, area, bar, scatter, bubble)
        baseOptions.xaxis = {
          categories: data.categories || [],
          labels: { style: { fontSize: '12px' } }
        };
        baseOptions.yaxis = {
          labels: { style: { fontSize: '12px' } }
        };
        
        if (['line', 'area'].includes(type)) {
          baseOptions.stroke = { curve: 'smooth', width: 3 };
          baseOptions.markers = { size: 4 };
        }
        
        if (type === 'area') {
          baseOptions.fill = {
            type: 'gradient',
            gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3 }
          };
        }
        break;
    }

    return baseOptions;
  }, [type, title, data, height]);

  if (!data?.series || data.series.length === 0) {
    return (
      <div className="chatbot-chart-error" style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: '#666',
        fontSize: '14px',
        fontFamily: 'inherit'
      }}>
        <p>No data available for chart</p>
      </div>
    );
  }

  // Validate data based on chart type
  if (type === 'heatmap') {
    const hasValidData = data.series.some(series => 
      series.data && series.data.length > 0 && 
      series.data.some((d: any) => d.x !== undefined && d.y !== undefined)
    );
    if (!hasValidData) {
      return (
        <div className="chatbot-chart-error" style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666',
          fontSize: '14px',
          fontFamily: 'inherit'
        }}>
          <p>Invalid heatmap data format. Expected: [{`{x: "label", y: value}`}]</p>
        </div>
      );
    }
  }

  return (
    <div className="chatbot-chart-container" style={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box', height: `${adjustedHeight}px` }}>
      <Chart
        options={chartOptions}
        series={data.series}
        type={type as any}
        height={adjustedHeight}
        width="100%"
key={`${type}-${JSON.stringify(data.series).slice(0, 50)}`}
      />
    </div>
  );
};

export default memo(ChartRenderer);